import { lazy, Suspense } from 'react';
import { Route, Routes, Outlet } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PlatformLayout } from '@/layouts/PlatformLayout';
import { PageLoader } from '@/components/PageLoader';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';

// ============================================================
// Lazy-loaded pages — each route is a separate chunk so the
// initial bundle stays small. Vite naturally groups by import()
// call site; co-located WO/devices pages share their folder chunk.
// ============================================================

// Auth
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const InviteAccept = lazy(() => import('@/pages/InviteAccept'));
const SocialCallback = lazy(() => import('@/pages/SocialCallback'));
const Members = lazy(() => import('@/pages/settings/Members'));
const ProfilePage = lazy(() => import('@/pages/Profile'));
const NotificationPreferences = lazy(() => import('@/pages/settings/NotificationPreferences'));
const PermissionsAdmin = lazy(() => import('@/pages/settings/PermissionsAdmin'));
const IamAudit = lazy(() => import('@/pages/settings/IamAudit'));
const OrganizationSettings = lazy(() => import('@/pages/settings/OrganizationSettings'));
const SecurityPage = lazy(() => import('@/pages/account/Security'));

// Entry / system
const Index = lazy(() => import('@/pages/Index'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const OnboardingWizard = lazy(() => import('@/pages/onboarding/OnboardingWizard'));

// Production
const DispatchBoard = lazy(() => import('@/pages/DispatchBoard'));
const AssemblyVerification = lazy(() => import('@/pages/AssemblyVerification'));
const CuttingGuidance = lazy(() => import('@/pages/CuttingGuidance'));
const Cobotics = lazy(() => import('@/pages/Cobotics'));
const WIPBoard = lazy(() => import('@/pages/WIPBoard'));
const WorkOrderDetailPage = lazy(() => import('@/pages/production/wo/WorkOrderDetail'));
const WorkOrderTimelinePage = lazy(() => import('@/pages/production/wo/WorkOrderTimeline'));
const WorkOrderBomPage = lazy(() => import('@/pages/production/wo/WorkOrderBom'));
const WorkOrderRoutingPage = lazy(() => import('@/pages/production/wo/WorkOrderRouting'));
const WorkOrderCostsPage = lazy(() => import('@/pages/production/wo/WorkOrderCosts'));
const WorkOrderOperatorsPage = lazy(() => import('@/pages/production/wo/WorkOrderOperators'));
const WorkOrderProgressPage = lazy(() => import('@/pages/production/wo/WorkOrderProgress'));
const WorkOrderDocsPage = lazy(() => import('@/pages/production/wo/WorkOrderDocs'));
const WorkOrderCertsPage = lazy(() => import('@/pages/production/wo/WorkOrderCerts'));
const WorkOrderVariancePage = lazy(() => import('@/pages/production/wo/WorkOrderVariance'));
const WorkOrderBottleneckPage = lazy(() => import('@/pages/production/wo/WorkOrderBottleneck'));
const WorkOrderDelayRiskPage = lazy(() => import('@/pages/production/wo/WorkOrderDelayRisk'));

// Quality
const QualityBoard = lazy(() => import('@/pages/QualityBoard'));
const DefectDetection = lazy(() => import('@/pages/DefectDetection'));
const Vision3D = lazy(() => import('@/pages/Vision3D'));
const QualityAnalytics = lazy(() => import('@/pages/QualityAnalytics'));
const PackagingVerification = lazy(() => import('@/pages/PackagingVerification'));
const CameraCalibration = lazy(() => import('@/pages/CameraCalibration'));

// Logistics
const TraceabilitySearch = lazy(() => import('@/pages/TraceabilitySearch'));
const WarehouseMap = lazy(() => import('@/pages/WarehouseMap'));
const InventoryManagement = lazy(() => import('@/pages/InventoryManagement'));
const BarcodeAnalysis = lazy(() => import('@/pages/BarcodeAnalysis'));

// Reports / Audit
const ReportsDashboard = lazy(() => import('@/pages/ReportsDashboard'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));

// Maintenance
const PredictiveMaintenance = lazy(() => import('@/pages/PredictiveMaintenance'));
const ConditionMonitoring = lazy(() => import('@/pages/ConditionMonitoring'));
const SafetyCompliance = lazy(() => import('@/pages/SafetyCompliance'));

// BackOffice — Devices L1..L5
const DeviceManager = lazy(() => import('@/pages/DeviceManager'));
const CameraDetailPage = lazy(() => import('@/pages/devices/CameraDetail'));
const CameraLivePage = lazy(() => import('@/pages/devices/CameraLive'));
const CameraKpisPage = lazy(() => import('@/pages/devices/CameraKpis'));
const CameraCalibrationPage = lazy(() => import('@/pages/devices/CameraCalibration'));
const CameraDetectionsPage = lazy(() => import('@/pages/devices/CameraDetections'));
const CameraConfigPage = lazy(() => import('@/pages/devices/CameraConfig'));
const CameraHealthPage = lazy(() => import('@/pages/devices/CameraHealth'));
const CameraComparePage = lazy(() => import('@/pages/devices/CameraCompare'));
const EdgeDetailPage = lazy(() => import('@/pages/devices/EdgeDetail'));
const EdgeMetricsPage = lazy(() => import('@/pages/devices/EdgeMetrics'));
const PLCDetailPage = lazy(() => import('@/pages/devices/PLCDetail'));
const DeviceFirmwarePage = lazy(() => import('@/pages/devices/DeviceFirmware'));
const NetworkTopologyPage = lazy(() => import('@/pages/devices/NetworkTopology'));
const FloorMapPage = lazy(() => import('@/pages/devices/FloorMap'));
const SLAPage = lazy(() => import('@/pages/devices/SLA'));

// BackOffice — Platform admin
const RulesEngine = lazy(() => import('@/pages/RulesEngine'));
const Billing = lazy(() => import('@/pages/Billing'));
const ERPSync = lazy(() => import('@/pages/ERPSync'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));

// Observability / shared
const CopilotPage = lazy(() => import('@/pages/Copilot'));
const AlertsPage = lazy(() => import('@/pages/Alerts'));
const StoragePage = lazy(() => import('@/pages/Storage'));
const CachePage = lazy(() => import('@/pages/Cache'));
const AnalyticsPage = lazy(() => import('@/pages/Analytics'));
const APMPage = lazy(() => import('@/pages/APM'));
const IncidentsPage = lazy(() => import('@/pages/Incidents'));
const ErrorMonitorPage = lazy(() => import('@/pages/ErrorMonitor'));

// MLOps
const SAM2Studio = lazy(() => import('@/pages/SAM2Studio'));
const ModelFactory = lazy(() => import('@/pages/ModelFactory'));
const VideoInference = lazy(() => import('@/pages/VideoInference'));
const DriftMonitoring = lazy(() => import('@/pages/DriftMonitoring'));
const ZeroShotExplorer = lazy(() => import('@/pages/ZeroShotExplorer'));
const DatasetCuration = lazy(() => import('@/pages/DatasetCuration'));
const VectorSearch = lazy(() => import('@/pages/VectorSearch'));
const ExperimentComparison = lazy(() => import('@/pages/ExperimentComparison'));
const ONNXPipeline = lazy(() => import('@/pages/ONNXPipeline'));
const VisionIndustrial = lazy(() => import('@/pages/VisionIndustrial'));
const InferenceFeedback = lazy(() => import('@/pages/InferenceFeedback'));
const ClosedLoopHealth = lazy(() => import('@/pages/mlops/ClosedLoopHealth'));

// Omniverse
const OmniverseHub = lazy(() => import('@/pages/omniverse/OmniverseHub'));
const OmniverseConvert = lazy(() => import('@/pages/omniverse/OmniverseConvert'));
const OmniverseGenerate = lazy(() => import('@/pages/omniverse/OmniverseGenerate'));
const OmniverseDatasets = lazy(() => import('@/pages/omniverse/OmniverseDatasets'));

// Live Camera Inference
const LiveCameraInference = lazy(() => import('@/pages/LiveCameraInference'));

// FAI Lab
const FaiDossier = lazy(() => import('@/pages/fai/FaiDossier'));

// Admin
const ModulesSettings = lazy(() => import('@/pages/admin/ModulesSettings'));
const InferenceMappings = lazy(() => import('@/pages/admin/InferenceMappings'));
const ABTesting = lazy(() => import('@/pages/admin/ABTesting'));
const PromotionRequests = lazy(() => import('@/pages/admin/PromotionRequests'));

// Agent
const AgentSettings = lazy(() => import('@/pages/AgentSettings'));

// Settings (Connections + Files)
const ConnectionsPage = lazy(() => import('@/pages/settings/ConnectionsPage'));
const FilesPage = lazy(() => import('@/pages/settings/FilesPage'));

const adminSup = ['admin', 'supervisor'] as const;
const adminOnly = ['admin'] as const;

/**
 * Production routes — Dispatch, assembly, cutting, cobotics, WIP and the
 * full Work Order drill-down (L6..L9, M2 closure). Accessible to all roles.
 */
function ProductionRoutes() {
  return (
    <>
      <Route path="/production/dispatch" element={<DispatchBoard />} />
      <Route path="/production/assembly" element={<AssemblyVerification />} />
      <Route path="/production/cutting" element={<CuttingGuidance />} />
      <Route path="/production/cobotics" element={<Cobotics />} />
      <Route path="/production/wip" element={<WIPBoard />} />
      <Route path="/production/work-orders/:id" element={<WorkOrderDetailPage />} />
      <Route path="/production/work-orders/:id/timeline" element={<WorkOrderTimelinePage />} />
      <Route path="/production/work-orders/:id/bom" element={<WorkOrderBomPage />} />
      <Route path="/production/work-orders/:id/routing" element={<WorkOrderRoutingPage />} />
      <Route path="/production/work-orders/:id/costs" element={<WorkOrderCostsPage />} />
      <Route path="/production/work-orders/:id/operators" element={<WorkOrderOperatorsPage />} />
      <Route path="/production/work-orders/:id/progress" element={<WorkOrderProgressPage />} />
      <Route path="/production/work-orders/:id/docs" element={<WorkOrderDocsPage />} />
      <Route path="/production/work-orders/:id/certs" element={<WorkOrderCertsPage />} />
      <Route path="/production/work-orders/:id/variance" element={<WorkOrderVariancePage />} />
      <Route path="/production/work-orders/:id/bottleneck" element={<WorkOrderBottleneckPage />} />
      <Route path="/production/work-orders/:id/delay-risk" element={<WorkOrderDelayRiskPage />} />
    </>
  );
}

/**
 * Quality routes — board, defects, 3D vision, analytics, packaging, calibration.
 * Restricted views require admin or supervisor role.
 */
function QualityRoutes() {
  return (
    <>
      <Route path="/quality/board" element={<ProtectedRoute requiredRoles={[...adminSup]}><QualityBoard /></ProtectedRoute>} />
      <Route path="/quality/defect-detection" element={<DefectDetection />} />
      <Route path="/quality/vision-3d" element={<Vision3D />} />
      <Route path="/quality/analytics" element={<ProtectedRoute requiredRoles={[...adminSup]}><QualityAnalytics /></ProtectedRoute>} />
      <Route path="/quality/packaging" element={<PackagingVerification />} />
      <Route path="/quality/calibration" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraCalibration /></ProtectedRoute>} />
    </>
  );
}

/**
 * Logistics, materials, traceability and analytical reports (GS1-128 / SSCC-18).
 */
function LogisticsRoutes() {
  return (
    <>
      <Route path="/traceability/search" element={<TraceabilitySearch />} />
      <Route path="/logistics/warehouse" element={<WarehouseMap />} />
      <Route path="/materials/inventory" element={<InventoryManagement />} />
      <Route path="/materials/barcode" element={<BarcodeAnalysis />} />
      <Route path="/analytics/reports" element={<ProtectedRoute requiredRoles={[...adminSup]}><ReportsDashboard /></ProtectedRoute>} />
      <Route path="/system/audit-log" element={<ProtectedRoute requiredRoles={[...adminOnly]}><AuditLog /></ProtectedRoute>} />
    </>
  );
}

/** Maintenance — Predictive, condition monitoring, safety/compliance. */
function MaintenanceRoutes() {
  return (
    <>
      <Route path="/maintenance/predictive" element={<PredictiveMaintenance />} />
      <Route path="/maintenance/condition" element={<ConditionMonitoring />} />
      <Route path="/maintenance/safety" element={<SafetyCompliance />} />
    </>
  );
}

/**
 * BackOffice routes — Devices (L1..L5 drill-down for cameras, edge nodes,
 * PLCs and firmware) plus platform admin (rules, billing, ERP sync, admin
 * panel) and the observability/shared utilities. Most routes require admin.
 */
function BackOfficeRoutes() {
  return (
    <>
      {/* Devices — index + L1..L5 */}
      <Route path="/backoffice/devices" element={<ProtectedRoute requiredRoles={[...adminOnly]}><DeviceManager /></ProtectedRoute>} />
      {/* L1 — Cameras (canonical plural) */}
      <Route path="/backoffice/devices/cameras/:id" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraDetailPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/live" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraLivePage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/kpis" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraKpisPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/calibration" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraCalibrationPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/detections" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraDetectionsPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/config" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraConfigPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/:id/health" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraHealthPage /></ProtectedRoute>} />
      {/* Backwards-compat: legacy singular paths */}
      <Route path="/backoffice/devices/camera/:id" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraDetailPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/live" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraLivePage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/kpis" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraKpisPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/calibration" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraCalibrationPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/detections" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraDetectionsPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/config" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraConfigPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/camera/:id/health" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraHealthPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/cameras/compare" element={<ProtectedRoute requiredRoles={[...adminSup]}><CameraComparePage /></ProtectedRoute>} />
      {/* L3..L4 — Edge / PLC / Firmware */}
      <Route path="/backoffice/devices/edge/:id" element={<ProtectedRoute requiredRoles={[...adminSup]}><EdgeDetailPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/edge/:id/metrics" element={<ProtectedRoute requiredRoles={[...adminSup]}><EdgeMetricsPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/plc/:id" element={<ProtectedRoute requiredRoles={[...adminSup]}><PLCDetailPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/:type/:id/firmware" element={<ProtectedRoute requiredRoles={[...adminSup]}><DeviceFirmwarePage /></ProtectedRoute>} />
      {/* L5 — Systemic views */}
      <Route path="/backoffice/devices/network-topology" element={<ProtectedRoute requiredRoles={[...adminSup]}><NetworkTopologyPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/floor-map" element={<ProtectedRoute requiredRoles={[...adminSup]}><FloorMapPage /></ProtectedRoute>} />
      <Route path="/backoffice/devices/sla" element={<ProtectedRoute requiredRoles={[...adminSup]}><SLAPage /></ProtectedRoute>} />
      {/* Platform admin */}
      <Route path="/backoffice/rules" element={<ProtectedRoute requiredRoles={[...adminOnly]}><RulesEngine /></ProtectedRoute>} />
      <Route path="/backoffice/billing" element={<ProtectedRoute requiredRoles={[...adminOnly]}><Billing /></ProtectedRoute>} />
      <Route path="/backoffice/erp-sync" element={<ProtectedRoute requiredRoles={[...adminOnly]}><ERPSync /></ProtectedRoute>} />
      <Route path="/backoffice/admin" element={<ProtectedRoute requiredRoles={[...adminOnly]}><AdminPanel /></ProtectedRoute>} />
      {/* Shared observability */}
      <Route path="/copilot" element={<ProtectedRoute><CopilotPage /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
      <Route path="/storage" element={<ProtectedRoute><StoragePage /></ProtectedRoute>} />
      <Route path="/cache" element={<ProtectedRoute><CachePage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/apm" element={<ProtectedRoute><APMPage /></ProtectedRoute>} />
      <Route path="/incidents" element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>} />
      <Route path="/errors" element={<ProtectedRoute><ErrorMonitorPage /></ProtectedRoute>} />
    </>
  );
}

/** MLOps — SAM2, Model Factory, drift, zero-shot, dataset curation, vector search, experiments, ONNX, vision industrial. */
function MLOpsRoutes() {
  return (
    <>
      <Route path="/mlops/sam2" element={<ProtectedRoute requiredRoles={[...adminSup]}><SAM2Studio /></ProtectedRoute>} />
      <Route path="/mlops/model-factory" element={<ProtectedRoute requiredRoles={[...adminSup]}><ModelFactory /></ProtectedRoute>} />
      <Route path="/mlops/video-inference" element={<ProtectedRoute requiredRoles={[...adminSup]}><VideoInference /></ProtectedRoute>} />
      <Route path="/mlops/drift" element={<ProtectedRoute requiredRoles={[...adminSup]}><DriftMonitoring /></ProtectedRoute>} />
      <Route path="/mlops/zero-shot" element={<ProtectedRoute requiredRoles={[...adminSup]}><ZeroShotExplorer /></ProtectedRoute>} />
      <Route path="/mlops/dataset-qa" element={<ProtectedRoute requiredRoles={[...adminSup]}><DatasetCuration /></ProtectedRoute>} />
      <Route path="/mlops/vector-search" element={<ProtectedRoute requiredRoles={[...adminSup]}><VectorSearch /></ProtectedRoute>} />
      <Route path="/mlops/experiments" element={<ProtectedRoute requiredRoles={[...adminSup]}><ExperimentComparison /></ProtectedRoute>} />
      <Route path="/mlops/onnx-pipeline" element={<ProtectedRoute requiredRoles={[...adminSup]}><ONNXPipeline /></ProtectedRoute>} />
      <Route path="/mlops/vision-industrial" element={<ProtectedRoute requiredRoles={[...adminSup]}><VisionIndustrial /></ProtectedRoute>} />
      <Route path="/mlops/feedback" element={<ProtectedRoute requiredRoles={[...adminSup]}><InferenceFeedback /></ProtectedRoute>} />
      <Route path="/mlops/closed-loop" element={<ProtectedRoute requiredRoles={[...adminSup]}><ClosedLoopHealth /></ProtectedRoute>} />
      {/* Live Camera Inference */}
      <Route path="/mlops/live-inference" element={<ProtectedRoute requiredRoles={[...adminSup]}><LiveCameraInference /></ProtectedRoute>} />
    </>
  );
}

/** Omniverse — Hub, CAD conversion, synthetic dataset generation, dataset listing. */
function OmniverseRoutes() {
  return (
    <>
      <Route path="/omniverse" element={<ProtectedRoute requiredRoles={[...adminSup]}><OmniverseHub /></ProtectedRoute>} />
      <Route path="/omniverse/convert" element={<ProtectedRoute requiredRoles={[...adminSup]}><OmniverseConvert /></ProtectedRoute>} />
      <Route path="/omniverse/generate" element={<ProtectedRoute requiredRoles={[...adminSup]}><OmniverseGenerate /></ProtectedRoute>} />
      <Route path="/omniverse/datasets" element={<ProtectedRoute requiredRoles={[...adminSup]}><OmniverseDatasets /></ProtectedRoute>} />
    </>
  );
}

/**
 * Top-level router for the EXPAI MES platform.
 *
 * Layered structure:
 *  - Public auth routes (login/register/password).
 *  - Protected catch-all wrapped in {@link PlatformLayout}, with module-scoped
 *    route groups (Production, Quality, Logistics, Maintenance, BackOffice, MLOps).
 *  - All route components are lazy-loaded; {@link PageLoader} bridges chunks.
 */
export function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/social" element={<SocialCallback />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <OnboardingWizard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <PlatformLayout>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Top-level pages — wrap individually so any render error
                        stays contained to the affected page. */}
                    <Route element={<RouteErrorBoundary scope="Inicio"><Outlet /></RouteErrorBoundary>}>
                      <Route path="/" element={<Index />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                    </Route>

                    {/* Module-scoped boundaries: a crash in one module never
                        unmounts the rest of the platform shell. */}
                    <Route element={<RouteErrorBoundary scope="Producción"><Outlet /></RouteErrorBoundary>}>
                      {ProductionRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Calidad"><Outlet /></RouteErrorBoundary>}>
                      {QualityRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Logística"><Outlet /></RouteErrorBoundary>}>
                      {LogisticsRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Mantenimiento"><Outlet /></RouteErrorBoundary>}>
                      {MaintenanceRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="BackOffice"><Outlet /></RouteErrorBoundary>}>
                      {BackOfficeRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="MLOps"><Outlet /></RouteErrorBoundary>}>
                      {MLOpsRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Omniverse"><Outlet /></RouteErrorBoundary>}>
                      {OmniverseRoutes()}
                    </Route>
                    <Route element={<RouteErrorBoundary scope="FAI Lab"><Outlet /></RouteErrorBoundary>}>
                      <Route path="/fai/dossier" element={<ProtectedRoute requiredRoles={[...adminSup]}><FaiDossier /></ProtectedRoute>} />
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Admin"><Outlet /></RouteErrorBoundary>}>
                      <Route path="/admin/modules" element={<ProtectedRoute requiredRoles={[...adminOnly]}><ModulesSettings /></ProtectedRoute>} />
                      <Route path="/admin/inference/mappings" element={<ProtectedRoute requiredRoles={[...adminOnly]}><InferenceMappings /></ProtectedRoute>} />
                      <Route path="/admin/inference/ab-testing" element={<ProtectedRoute requiredRoles={[...adminOnly]}><ABTesting /></ProtectedRoute>} />
                      <Route path="/admin/mlops/promotion-requests" element={<ProtectedRoute requiredRoles={[...adminOnly]}><PromotionRequests /></ProtectedRoute>} />
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Agent"><Outlet /></RouteErrorBoundary>}>
                      <Route path="/agent/settings" element={<AgentSettings />} />
                    </Route>
                    <Route element={<RouteErrorBoundary scope="Settings"><Outlet /></RouteErrorBoundary>}>
                      <Route path="/settings/connections" element={<ProtectedRoute requiredRoles={[...adminOnly]}><ConnectionsPage /></ProtectedRoute>} />
                      <Route path="/settings/files" element={<ProtectedRoute requiredRoles={[...adminSup]}><FilesPage /></ProtectedRoute>} />
                      <Route path="/settings/members" element={<ProtectedRoute requiredRoles={['owner','admin']}><Members /></ProtectedRoute>} />
                      <Route path="/settings/organization" element={<ProtectedRoute requiredRoles={['owner','admin']}><OrganizationSettings /></ProtectedRoute>} />
                      <Route path="/settings/permissions" element={<ProtectedRoute requiredRoles={['owner','admin']}><PermissionsAdmin /></ProtectedRoute>} />
                      <Route path="/settings/audit" element={<ProtectedRoute requiredRoles={['owner','admin']}><IamAudit /></ProtectedRoute>} />
                      <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
                      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                      <Route path="/account/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </PlatformLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
